export const supportedCompetitionVideoExt = [
  "mp4",
  "avi",
  "mov",
  "wmv",
  "mkv",
  "webm",
  "mpeg",
  "mpg",
  "3gp",
];

export const supportedCompetitionImageExt = [
  "jpg",
  "jpeg",
  "png",
  "webp",
  "gif",
  "heic",
  "heif",
];

export const competitionMediaAccept =
  "video/*,image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif";

/** File picker: videos only (fashion dress video, etc.) */
export const dressVideoAccept = [
  ...supportedCompetitionVideoExt.map((ext) => `.${ext}`),
  "video/mp4",
  "video/quicktime",
  "video/webm",
  "video/x-msvideo",
  "video/3gpp",
  "video/mpeg",
].join(",");

/** File picker: images only (fashion dress image, banners, etc.) */
export const dressImageAccept = [
  ...supportedCompetitionImageExt.map((ext) => `.${ext}`),
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/heic",
  "image/heif",
].join(",");

export function getFileExtension(file) {
  if (!file?.name) return "";
  return file.name.split(".").pop()?.toLowerCase() || "";
}

export function isCompetitionImageFile(file) {
  if (!file) return false;
  const ext = getFileExtension(file);
  const typePart = file.type?.split("/")[1]?.replace("jpeg", "jpg");
  return (
    supportedCompetitionImageExt.includes(ext) ||
    (typePart && supportedCompetitionImageExt.includes(typePart))
  );
}

export function isCompetitionVideoFile(file) {
  if (!file) return false;
  const ext = getFileExtension(file);
  const typePart = file.type?.split("/")[1];
  return (
    supportedCompetitionVideoExt.includes(ext) ||
    (typePart && supportedCompetitionVideoExt.includes(typePart))
  );
}

export function inferMediaTypeFromUrl(url = "") {
  const clean = String(url).split("?")[0].split("#")[0].toLowerCase();
  const ext = clean.split(".").pop() || "";
  if (supportedCompetitionImageExt.includes(ext)) return "image";
  if (supportedCompetitionVideoExt.includes(ext)) return "video";
  return "video";
}

export function resolveCompetitionMediaType(item) {
  if (!item) return "video";
  const explicit = item.media_type || item.mediaType;
  if (explicit === "image" || explicit === "video") return explicit;
  return inferMediaTypeFromUrl(item.video_url || item.thumbnail);
}

export function isCompetitionImageMedia(item) {
  return resolveCompetitionMediaType(item) === "image";
}

/** Poster / listing thumb for competition uploads (videos and images). */
export function resolveCompetitionVideoThumbnail(item) {
  if (!item) return "";
  const url =
    item.thumbnail ||
    item.thumb ||
    item.poster ||
    item.thumbnail_url ||
    "";
  return String(url).trim();
}

/** Video poster: upload thumb, else competition banner. */
export function resolveCompetitionMediaPoster(item, competitionBanner) {
  const thumb = resolveCompetitionVideoThumbnail(item);
  if (thumb) return thumb;
  return String(competitionBanner || "").trim();
}

export function competitionMediaPreviewSrc(item) {
  if (!item) return "";
  if (isCompetitionImageMedia(item)) {
    return item.video_url || resolveCompetitionVideoThumbnail(item);
  }
  return resolveCompetitionVideoThumbnail(item);
}

/** Seconds into the file to use for a still preview. */
export const COMPETITION_VIDEO_PREVIEW_SEC = 0.002;

export function competitionVideoPreviewTime(durationSec) {
  const duration = Number(durationSec);
  if (
    Number.isFinite(duration) &&
    duration > 0 &&
    duration <= COMPETITION_VIDEO_PREVIEW_SEC
  ) {
    return 0;
  }
  return COMPETITION_VIDEO_PREVIEW_SEC;
}

/** Video URL with media fragment so the browser loads a frame near t. */
export function competitionVideoSrcWithPreviewTime(
  url,
  t = competitionVideoPreviewTime()
) {
  if (!url) return "";
  const base = String(url).split("#")[0];
  return `${base}#t=${t}`;
}

/**
 * Decode and show a still frame (muted micro-play). Returns false if blocked.
 */
export async function primeVideoElementFrame(el, time = competitionVideoPreviewTime()) {
  if (!el) return false;
  const prevMuted = el.muted;
  el.muted = true;

  const waitCanPlay = () =>
    new Promise((resolve, reject) => {
      if (el.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
        resolve();
        return;
      }
      const onCanPlay = () => {
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        reject(new Error("video error"));
      };
      const cleanup = () => {
        el.removeEventListener("canplay", onCanPlay);
        el.removeEventListener("error", onError);
      };
      el.addEventListener("canplay", onCanPlay);
      el.addEventListener("error", onError);
    });

  try {
    await waitCanPlay();
    try {
      el.currentTime = time;
    } catch {
      /* seek */
    }
    await el.play();
    await new Promise((r) => {
      requestAnimationFrame(() => requestAnimationFrame(r));
    });
    el.pause();
    try {
      if (Math.abs(el.currentTime - time) > 0.002) el.currentTime = time;
    } catch {
      /* seek */
    }
    el.muted = prevMuted;
    return true;
  } catch {
    el.muted = prevMuted;
    return false;
  }
}
